/**
 * Voice Activity Detection (VAD) Service
 * Fully adaptive VAD that automatically adjusts to any environment
 */

class VADService {
  constructor(options = {}) {
    // Base configuration
    this.calibrationDuration = options.calibrationDuration || 1500;
    this.baseNoiseMargin = options.noiseMargin || 1.4;
    this.speechThresholdMin = options.speechThresholdMin || 0.008;
    this.hangoverTime = options.hangoverTime || 400;

    // Adaptive parameters
    this.noiseMargin = this.baseNoiseMargin;
    this.minNoiseMargin = 1.15;  // Minimum sensitivity (very sensitive)
    this.maxNoiseMargin = 2.5;   // Maximum sensitivity (less sensitive)
    this.adaptationRate = 0.005; // Noise floor adaptation speed
    this.marginAdaptRate = 0.1;  // How fast we adjust sensitivity

    // State
    this.isCalibrating = true;
    this.calibrationSamples = [];
    this.calibrationStartTime = null;
    this.noiseFloor = 0.008;
    this.speechThreshold = 0.015;
    this.isSpeaking = false;
    this.lastSpeechTime = 0;
    this.smoothedEnergy = 0;
    this.energySmoothing = 0.15; // Fast response

    // Adaptive tracking
    this.speechEvents = [];        // Track recent speech events
    this.silenceEvents = [];       // Track silence periods
    this.peakEnergy = 0;           // Track peak energy seen
    this.recentEnergies = [];      // Rolling window of energy values
    this.lastAdaptTime = 0;
    this.adaptInterval = 5000;     // Check adaptation every 5s
    this.environmentChangeThreshold = 3; // Multiplier for detecting env change

    // Callbacks
    this.onSpeechStart = null;
    this.onSpeechEnd = null;
    this.onCalibrationComplete = null;
    this.onEnergyUpdate = null;
    this.onAdaptation = null;
  }

  start() {
    this.isCalibrating = true;
    this.calibrationSamples = [];
    this.calibrationStartTime = Date.now();
    this.isSpeaking = false;
    this.smoothedEnergy = 0;
    this.speechEvents = [];
    this.silenceEvents = [];
    this.recentEnergies = [];
    this.peakEnergy = 0;
    this.lastAdaptTime = Date.now();
    console.log('[VAD] Starting calibration...');
  }

  process(audioData) {
    const energy = this.calculateRMS(audioData);
    const now = Date.now();

    // Smooth energy
    this.smoothedEnergy = this.energySmoothing * energy +
                          (1 - this.energySmoothing) * this.smoothedEnergy;

    // Track recent energies for adaptation
    this.recentEnergies.push(this.smoothedEnergy);
    if (this.recentEnergies.length > 100) {
      this.recentEnergies.shift();
    }

    // Track peak energy
    if (this.smoothedEnergy > this.peakEnergy) {
      this.peakEnergy = this.smoothedEnergy;
    }

    // Emit energy update
    if (this.onEnergyUpdate) {
      this.onEnergyUpdate({
        energy: this.smoothedEnergy,
        noiseFloor: this.noiseFloor,
        threshold: this.speechThreshold,
        isSpeaking: this.isSpeaking,
        isCalibrating: this.isCalibrating,
        noiseMargin: this.noiseMargin
      });
    }

    // Calibration phase
    if (this.isCalibrating) {
      this.calibrationSamples.push(energy);
      if (now - this.calibrationStartTime >= this.calibrationDuration) {
        this.completeCalibration();
      }
      return false;
    }

    // Detect sudden environment change (e.g., moved to noisy place)
    this.detectEnvironmentChange();

    // Speech detection with hysteresis
    const wasSpeaking = this.isSpeaking;

    if (this.smoothedEnergy > this.speechThreshold) {
      this.isSpeaking = true;
      this.lastSpeechTime = now;
    } else if (this.isSpeaking && (now - this.lastSpeechTime) > this.hangoverTime) {
      this.isSpeaking = false;
    }

    // Track speech/silence events for adaptation
    if (this.isSpeaking && !wasSpeaking) {
      this.speechEvents.push({ time: now, energy: this.smoothedEnergy });
      // Keep only last 20 events
      if (this.speechEvents.length > 20) this.speechEvents.shift();
    }
    if (!this.isSpeaking && wasSpeaking) {
      this.silenceEvents.push({ time: now, duration: now - this.lastSpeechTime });
      if (this.silenceEvents.length > 20) this.silenceEvents.shift();
    }

    // Adapt noise floor during confirmed silence
    if (!this.isSpeaking && this.smoothedEnergy < this.speechThreshold * 0.6) {
      // Adapt faster when noise increases, slower when decreases
      const rate = this.smoothedEnergy > this.noiseFloor
        ? this.adaptationRate * 3  // Fast increase
        : this.adaptationRate;     // Slow decrease

      this.noiseFloor = this.noiseFloor * (1 - rate) + this.smoothedEnergy * rate;
      this.updateThreshold();
    }

    // Periodic sensitivity adaptation
    if (now - this.lastAdaptTime > this.adaptInterval) {
      this.adaptSensitivity();
      this.lastAdaptTime = now;
    }

    // Emit events
    if (this.isSpeaking && !wasSpeaking) {
      console.log('[VAD] Speech started');
      if (this.onSpeechStart) this.onSpeechStart();
    } else if (!this.isSpeaking && wasSpeaking) {
      console.log('[VAD] Speech ended');
      if (this.onSpeechEnd) this.onSpeechEnd();
    }

    return this.isSpeaking;
  }

  /**
   * Detect if environment has changed significantly
   */
  detectEnvironmentChange() {
    if (this.recentEnergies.length < 50) return;

    // Calculate recent average
    const recentAvg = this.recentEnergies.slice(-30).reduce((a, b) => a + b, 0) / 30;

    // If recent average is much higher than noise floor, environment got noisier
    if (recentAvg > this.noiseFloor * this.environmentChangeThreshold) {
      console.log(`[VAD] Environment change detected: recentAvg=${recentAvg.toFixed(4)}, noiseFloor=${this.noiseFloor.toFixed(4)}`);

      // Quick adaptation: raise noise floor
      this.noiseFloor = recentAvg * 0.8;
      this.updateThreshold();

      // Also lower sensitivity to avoid constant triggering
      this.noiseMargin = Math.min(this.noiseMargin + 0.2, this.maxNoiseMargin);
      this.updateThreshold();

      if (this.onAdaptation) {
        this.onAdaptation({ type: 'environment_change', noiseFloor: this.noiseFloor, noiseMargin: this.noiseMargin });
      }
    }
  }

  /**
   * Adapt sensitivity based on speech detection patterns
   */
  adaptSensitivity() {
    const now = Date.now();

    // Count recent speech events (last 10 seconds)
    const recentSpeechEvents = this.speechEvents.filter(e => now - e.time < 10000);
    const speechRate = recentSpeechEvents.length;

    // Analyze speech event energies
    const avgSpeechEnergy = recentSpeechEvents.length > 0
      ? recentSpeechEvents.reduce((a, b) => a + b.energy, 0) / recentSpeechEvents.length
      : 0;

    let adapted = false;

    // Too few speech events? Lower threshold (more sensitive)
    if (speechRate < 2 && this.noiseMargin > this.minNoiseMargin) {
      this.noiseMargin = Math.max(this.noiseMargin - this.marginAdaptRate, this.minNoiseMargin);
      adapted = true;
      console.log(`[VAD] Adapting: too few events (${speechRate}), lowering margin to ${this.noiseMargin.toFixed(2)}`);
    }
    // Too many speech events? Might be noise triggering - raise threshold
    else if (speechRate > 15 && this.noiseMargin < this.maxNoiseMargin) {
      this.noiseMargin = Math.min(this.noiseMargin + this.marginAdaptRate, this.maxNoiseMargin);
      adapted = true;
      console.log(`[VAD] Adapting: too many events (${speechRate}), raising margin to ${this.noiseMargin.toFixed(2)}`);
    }
    // If speech energy is barely above threshold, lower margin
    else if (avgSpeechEnergy > 0 && avgSpeechEnergy < this.speechThreshold * 1.3 && this.noiseMargin > this.minNoiseMargin) {
      this.noiseMargin = Math.max(this.noiseMargin - this.marginAdaptRate * 0.5, this.minNoiseMargin);
      adapted = true;
      console.log(`[VAD] Adapting: speech barely above threshold, lowering margin to ${this.noiseMargin.toFixed(2)}`);
    }

    if (adapted) {
      this.updateThreshold();
      if (this.onAdaptation) {
        this.onAdaptation({ type: 'sensitivity', noiseMargin: this.noiseMargin, speechRate });
      }
    }
  }

  calculateRMS(audioData) {
    let sum = 0;
    for (let i = 0; i < audioData.length; i++) {
      sum += audioData[i] * audioData[i];
    }
    return Math.sqrt(sum / audioData.length);
  }

  completeCalibration() {
    if (this.calibrationSamples.length === 0) {
      this.noiseFloor = this.speechThresholdMin;
    } else {
      // Use median for robustness
      const sorted = [...this.calibrationSamples].sort((a, b) => a - b);
      const median = sorted[Math.floor(sorted.length / 2)];
      const p75 = sorted[Math.floor(sorted.length * 0.75)];
      const p90 = sorted[Math.floor(sorted.length * 0.90)];

      // Use p75 as noise floor (more conservative)
      this.noiseFloor = Math.max(p75, this.speechThresholdMin / 2);

      // If there's high variance, environment is noisy - start with higher margin
      const variance = p90 / median;
      if (variance > 2) {
        this.noiseMargin = Math.min(this.baseNoiseMargin + 0.3, this.maxNoiseMargin);
        console.log(`[VAD] Noisy environment detected (variance=${variance.toFixed(2)}), using margin=${this.noiseMargin.toFixed(2)}`);
      }
    }

    this.updateThreshold();
    this.isCalibrating = false;
    this.lastAdaptTime = Date.now();

    console.log(`[VAD] Calibration complete: noiseFloor=${this.noiseFloor.toFixed(4)}, threshold=${this.speechThreshold.toFixed(4)}, margin=${this.noiseMargin.toFixed(2)}`);

    if (this.onCalibrationComplete) {
      this.onCalibrationComplete({
        noiseFloor: this.noiseFloor,
        threshold: this.speechThreshold,
        noiseMargin: this.noiseMargin
      });
    }
  }

  updateThreshold() {
    this.speechThreshold = Math.max(
      this.noiseFloor * this.noiseMargin,
      this.speechThresholdMin
    );
  }

  recalibrate() {
    console.log('[VAD] Recalibrating...');
    this.noiseMargin = this.baseNoiseMargin; // Reset margin
    this.start();
  }

  /**
   * Manually adjust sensitivity
   * @param {string} direction - 'increase' or 'decrease'
   */
  adjustSensitivity(direction) {
    if (direction === 'increase') {
      this.noiseMargin = Math.max(this.noiseMargin - 0.15, this.minNoiseMargin);
    } else {
      this.noiseMargin = Math.min(this.noiseMargin + 0.15, this.maxNoiseMargin);
    }
    this.updateThreshold();
    console.log(`[VAD] Manual adjustment: margin=${this.noiseMargin.toFixed(2)}, threshold=${this.speechThreshold.toFixed(4)}`);
    return { noiseMargin: this.noiseMargin, threshold: this.speechThreshold };
  }

  getState() {
    return {
      isSpeaking: this.isSpeaking,
      isCalibrating: this.isCalibrating,
      noiseFloor: this.noiseFloor,
      speechThreshold: this.speechThreshold,
      currentEnergy: this.smoothedEnergy,
      noiseMargin: this.noiseMargin,
      speechEventsCount: this.speechEvents.length
    };
  }

  stop() {
    this.isSpeaking = false;
    this.isCalibrating = false;
    console.log('[VAD] Stopped');
  }
}

// Singleton instance
let vadInstance = null;

export function getVAD(options) {
  if (!vadInstance) {
    vadInstance = new VADService(options);
  }
  return vadInstance;
}

export function resetVAD() {
  if (vadInstance) {
    vadInstance.stop();
    vadInstance = null;
  }
}

export default VADService;
